"use client"
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from "@/components/ui/collapsible"
import { usePathname } from "next/navigation"

import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import IconMinus from "@/app/(site)/_components/Atoms/Icons/IconMinus"
import IconPlus from "@/app/(site)/_components/Atoms/Icons/IconPlus"
import centraPath from "@/lib/centra-path/centra-path-init"
import clsx from "clsx"
import { useState } from "react"
import DynamicLink from "../../Molecules/Link"

function Heading({ children }: { children: React.ReactNode }) {
    const classes = clsx(
        "uppercase",
        "font-trimRegular",
        "mb-16",
        "flex",
        "gap-5",
        "items-center",
        "text-16",
        "md:text-12",
        "text-black"
    )
    return (
        <div className={classes}>
            <div className="h-12 w-12 bg-black md:h-8 md:w-8" />
            {children}
        </div>
    )
}

function MenuLink({
    item,
    isActive,
    isLast
}: {
    item: { uri: string; title: string }
    isActive: boolean
    isLast: boolean
}) {
    const linkClasses = clsx(
        "uppercase",
        "flex",
        "font-trimRegular",
        "border-l-2",
        "border-black",
        "pl-12",
        "text-16",
        "md:text-12",
        "group",
        isActive ? "text-black" : "text-main-dark-grey",
        "group-hover:text-black"
    )
    const arrowClasses = clsx(
        "transition-opacity",
        "duration-300",
        "opacity-0",
        "group-hover:opacity-100",
        "w-6",
        "h-6",
        "md:w-20",
        "md:h-12"
    )

    return (
        <DynamicLink href={item.uri} className={linkClasses} prefetch={false}>
            <div className={clsx("flex", "items-center", "gap-5", !isLast && "mb-6")}>
                <span>{item.title}</span>
                <IconArrowRight className={arrowClasses} />
            </div>
        </DynamicLink>
    )
}

interface Props {
    menu: {
        title: string
        items: { title: string; uri: string }[]
    }
}

export default function PageHeadTrimMenu({ menu }: Props) {
    const pathname = usePathname()
    const [isCollapsed, setIsCollapsed] = useState(true)

    const menuList = menu.items.map((item, i) => (
        <li key={`${item.title}-${item.uri}-${i}`}>
            <MenuLink
                item={item}
                isActive={item.uri === centraPath.stripLeadingLocale(pathname)}
                isLast={menu.items.length === i + 1}
            />
        </li>
    ))

    return (
        <>
            {/* Collapsible for smaller screens */}
            <div className="md:hidden">
                <Collapsible onOpenChange={isOpen => setIsCollapsed(!isOpen)}>
                    <CollapsibleTrigger asChild>
                        <button className="flex w-full justify-between border-t-2 border-black pt-17">
                            <Heading>{menu.title}</Heading>
                            <span className="font-bold">
                                {isCollapsed ? (
                                    <IconPlus className="h-20 w-20" />
                                ) : (
                                    <IconMinus className="h-20 w-20" />
                                )}
                            </span>
                        </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <ul>{menuList}</ul>
                    </CollapsibleContent>
                </Collapsible>
            </div>

            {/* Non-collapsible for md and larger screens */}
            <div className="hidden md:mb-24 md:block">
                <Heading>{menu.title}</Heading>
                <ul>{menuList}</ul>
            </div>
        </>
    )
}
